import { useState } from "react"
import {
  Box,
  HStack,
  Spinner,
  Stack,
  Switch,
  Table,
} from "@chakra-ui/react"
import { LuPlus, LuTrash2 } from "react-icons/lu"
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query"
import { Button, Text } from "@/components/ui"
import { useAuth } from "@/contexts"
import {
  getTeamsChannels,
  browseTeamsChannels,
  addTeamsChannel,
  removeTeamsChannel,
  toggleTeamsChannel,
  AvailableChannel,
} from "@/api/teams"

// ── Browse modal ─────────────────────────────────────────────────────────────

const BrowseModal = ({
  onClose,
  onAdd,
}: {
  onClose: () => void
  onAdd: (channel: AvailableChannel) => Promise<void>
}) => {
  const [search, setSearch] = useState("")
  const [adding, setAdding] = useState<string | null>(null)
  const { data: available = [], isLoading, error } = useQuery({
    queryKey: ["teams-channels-browse"],
    queryFn: browseTeamsChannels,
  })

  const filtered = available.filter(
    (c) =>
      c.teamName.toLowerCase().includes(search.toLowerCase()) ||
      c.channelName.toLowerCase().includes(search.toLowerCase())
  )

  const handleAdd = async (ch: AvailableChannel) => {
    const key = `${ch.teamId}:${ch.channelId}`
    setAdding(key)
    try {
      await onAdd(ch)
    } finally {
      setAdding(null)
    }
  }

  return (
    <Box
      position="fixed"
      inset={0}
      zIndex={1000}
      display="flex"
      alignItems="center"
      justifyContent="center"
      bg="blackAlpha.600"
      onClick={onClose}
    >
      <Box
        bg="card.bg"
        borderRadius="xl"
        boxShadow="xl"
        w="560px"
        maxH="70vh"
        display="flex"
        flexDirection="column"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <HStack px={6} pt={5} pb={4} borderBottomWidth="1px" borderColor="border.default">
          <Text fontWeight="semibold" fontSize="lg" flex={1}>Add Teams Channel</Text>
          <Button variant="ghost" size="sm" onClick={onClose}>✕</Button>
        </HStack>

        {/* Search */}
        <Box px={6} pt={4} pb={3}>
          <input
            autoFocus
            placeholder="Search teams or channels…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={{
              width: "100%",
              padding: "8px 12px",
              borderRadius: "8px",
              border: "1px solid var(--chakra-colors-border-default)",
              background: "var(--chakra-colors-card-bg)",
              color: "inherit",
              fontSize: "14px",
              outline: "none",
            }}
          />
        </Box>

        {/* Channel list */}
        <Box overflowY="auto" flex={1} px={6} pb={4}>
          {isLoading && (
            <HStack justify="center" py={8}><Spinner size="sm" /><Text fontSize="sm">Loading channels…</Text></HStack>
          )}
          {error && (
            <Text fontSize="sm" color="red.400" py={4}>
              Failed to load channels. Ensure Azure permissions are granted.
            </Text>
          )}
          {!isLoading && filtered.length === 0 && !error && (
            <Text fontSize="sm" color="text.muted" py={4}>No channels found.</Text>
          )}
          <Stack gap={1}>
            {filtered.map((ch) => {
              const key = `${ch.teamId}:${ch.channelId}`
              return (
                <HStack
                  key={key}
                  px={3}
                  py={2}
                  borderRadius="md"
                  _hover={{ bg: "hover.bg" }}
                  justify="space-between"
                >
                  <Stack gap={0}>
                    <Text fontSize="sm" fontWeight="medium">{ch.channelName}</Text>
                    <Text fontSize="xs" color="text.muted">{ch.teamName}</Text>
                  </Stack>
                  <Button
                    size="xs"
                    variant="outline"
                    loading={adding === key}
                    onClick={() => handleAdd(ch)}
                  >
                    Add
                  </Button>
                </HStack>
              )
            })}
          </Stack>
        </Box>
      </Box>
    </Box>
  )
}

// ── Main component ────────────────────────────────────────────────────────────

export const TeamsChannelsConfig = () => {
  const { user } = useAuth()
  const isAdmin = user?.email === "sean@esofund.com"
  const [showBrowse, setShowBrowse] = useState(false)
  const qc = useQueryClient()

  const { data: channels = [], isLoading } = useQuery({
    queryKey: ["teams-channels"],
    queryFn: getTeamsChannels,
  })

  const addMutation = useMutation({
    mutationFn: addTeamsChannel,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["teams-channels"] }),
  })

  const removeMutation = useMutation({
    mutationFn: removeTeamsChannel,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["teams-channels"] }),
  })

  const toggleMutation = useMutation({
    mutationFn: ({ id, enabled }: { id: number; enabled: boolean }) =>
      toggleTeamsChannel(id, enabled),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["teams-channels"] }),
  })

  return (
    <Box>
      <HStack mb={6} justify="space-between" align="center">
        <Stack gap={1}>
          <Text fontWeight="semibold" fontSize="lg">Teams Channels</Text>
          <Text fontSize="sm" color="text.muted">
            Channels included in the chat assistant's search context.
          </Text>
        </Stack>
        {isAdmin && (
          <Button
            size="sm"
            onClick={() => setShowBrowse(true)}
          >
            <LuPlus />
            Add Channel
          </Button>
        )}
      </HStack>

      {/* Azure setup note */}
      <Box
        mb={5}
        p={4}
        borderRadius="md"
        bg="blue.50"
        borderWidth="1px"
        borderColor="blue.200"
        _dark={{ bg: "blue.900", borderColor: "blue.700" }}
      >
        <Text fontSize="sm" color="blue.700" _dark={{ color: "blue.200" }}>
          <strong>One-time setup required:</strong> An Azure AD admin must grant{" "}
          <code>ChannelMessage.Read.All</code> and <code>Team.ReadBasic.All</code> application
          permissions for the ESO Core app registration, then click "Grant admin consent".
        </Text>
      </Box>

      {isLoading ? (
        <HStack py={8} justify="center">
          <Spinner size="sm" />
          <Text fontSize="sm" color="text.muted">Loading…</Text>
        </HStack>
      ) : channels.length === 0 ? (
        <Box
          py={12}
          textAlign="center"
          borderWidth="1px"
          borderStyle="dashed"
          borderColor="border.default"
          borderRadius="lg"
        >
          <Text fontSize="sm" color="text.muted">No channels configured yet.</Text>
          {isAdmin && (
            <Text fontSize="sm" color="text.muted" mt={1}>
              Click "Add Channel" to include a Teams channel in the chat assistant.
            </Text>
          )}
        </Box>
      ) : (
        <Table.Root size="sm" variant="outline" borderRadius="lg" overflow="hidden">
          <Table.Header>
            <Table.Row bg="card.bg">
              <Table.ColumnHeader>Channel</Table.ColumnHeader>
              <Table.ColumnHeader>Team</Table.ColumnHeader>
              <Table.ColumnHeader>Enabled</Table.ColumnHeader>
              {isAdmin && <Table.ColumnHeader w="60px" />}
            </Table.Row>
          </Table.Header>
          <Table.Body>
            {channels.map((ch) => (
              <Table.Row key={ch.id}>
                <Table.Cell>
                  <Text fontSize="sm" fontWeight="medium">{ch.channelName}</Text>
                </Table.Cell>
                <Table.Cell>
                  <Text fontSize="sm" color="text.muted">{ch.teamName}</Text>
                </Table.Cell>
                <Table.Cell>
                  <Switch
                    size="sm"
                    checked={ch.enabled}
                    disabled={!isAdmin || toggleMutation.isPending}
                    onCheckedChange={(e) =>
                      toggleMutation.mutate({ id: ch.id, enabled: e.checked })
                    }
                  />
                </Table.Cell>
                {isAdmin && (
                  <Table.Cell>
                    <Button
                      size="xs"
                      variant="ghost"
                      colorScheme="red"
                      loading={removeMutation.isPending}
                      onClick={() => removeMutation.mutate(ch.id)}
                      aria-label="Remove channel"
                    >
                      <LuTrash2 />
                    </Button>
                  </Table.Cell>
                )}
              </Table.Row>
            ))}
          </Table.Body>
        </Table.Root>
      )}

      {showBrowse && (
        <BrowseModal
          onClose={() => setShowBrowse(false)}
          onAdd={async (ch) => {
            await addMutation.mutateAsync(ch)
            setShowBrowse(false)
          }}
        />
      )}
    </Box>
  )
}
